import React, { useState } from 'react';
import { HelpCircle, Plus, Minus, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header22';
import Footer from './components/Footer22';

const FAQs22 = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [openIndex, setOpenIndex] = useState(null);

    const categories = [
        {
            label: 'Craft & Materials',
            questions: [
                {
                    question: 'Are your products genuinely handcrafted?',
                    answer: 'Yes, every product on MAATI is 100% handcrafted by skilled artisans using traditional techniques. Each piece comes with an authenticity certificate that traces it back to the artisan and craft cluster of origin. We work directly with weaver cooperatives and artisan families across 12 states in India. No machine reproductions are ever listed on our platform.'
                },
                {
                    question: 'Why do handcrafted products have slight variations?',
                    answer: 'Variations in colour, texture, and pattern placement are a natural and beautiful characteristic of handcrafted textiles. Unlike machine-made fabrics that produce identical results, handloom weaving and hand block printing involve human skill, natural dyes, and traditional tools that create subtle uniqueness in every piece. These variations are not defects \u2014 they are the hallmark of authentic craft and what makes your piece truly one-of-a-kind.'
                },
                {
                    question: 'What fabrics do you use?',
                    answer: 'We primarily work with natural, sustainably sourced fabrics including: Chanderi silk-cotton blends from Madhya Pradesh, pure Katan silk for Banarasi weaves, organic cotton for block prints and Khadi, handloom linen from West Bengal, and Mulberry silk for Ikat weaves. All our fabrics are either handwoven on traditional looms or hand-printed using wooden blocks. We prioritise natural dyes where possible and ensure all materials are skin-friendly and ethically sourced.'
                },
                {
                    question: 'What is a GI (Geographical Indication) tag?',
                    answer: 'A GI tag is a certification that identifies a product as originating from a specific geographic region, where a given quality, reputation, or characteristic is essentially attributable to that origin. For example, Banarasi silk from Varanasi, Chanderi weaves from Chanderi, and Pochampally Ikat from Telangana all carry GI tags. We are committed to sourcing only GI-certified crafts where applicable, ensuring authenticity and supporting the artisan communities of origin.'
                },
                {
                    question: 'How do I care for my handcrafted garments?',
                    answer: 'Care instructions vary by fabric and craft type. Generally: silk sarees and Banarasi pieces should be dry cleaned only; block print cotton can be hand-washed in cold water with mild detergent; Khadi becomes softer with each wash. Always store silk in muslin cloth away from direct sunlight. Avoid wringing handloom fabrics. Each product comes with specific care instructions on its tag and product page. If unsure, reach out to us on WhatsApp for personalised care advice.'
                }
            ]
        },
        {
            label: 'Orders',
            questions: [
                {
                    question: 'How do I place an order?',
                    answer: 'Browse our collection, select your preferred product, choose your size (refer to our size guide for the best fit), and click "Add to Cart". Proceed to checkout, enter your shipping address, choose your payment method, and confirm your order. You will receive an order confirmation email with your order number and estimated delivery date.'
                },
                {
                    question: 'Can I customise or personalise my order?',
                    answer: 'Yes! Many of our products can be customised. For custom sizing, specific colour combinations, or personalised embroidery, please contact us via WhatsApp or email with your requirements. Our design team will work with the artisan to create a piece tailored to your specifications. Custom orders typically take 15-30 days depending on the complexity of the craft. A 50% advance payment is required for custom orders.'
                },
                {
                    question: 'What payment methods do you accept?',
                    answer: 'We accept all major payment methods through our secure payment gateway, Razorpay: Credit cards (Visa, Mastercard, RuPay), Debit cards, UPI (Google Pay, PhonePe, Paytm), Net Banking, EMI options on select banks, Cash on Delivery (COD) available for orders up to \u20B910,000 within India. All online transactions are secured with 256-bit SSL encryption.'
                },
                {
                    question: 'Can I cancel my order?',
                    answer: 'Orders can be cancelled within 24 hours of placement if they have not yet been dispatched. For ready-to-ship items, cancellation is subject to the order not having been handed over to our logistics partner. For made-to-order items, cancellation is possible only if production has not yet begun. Please contact us immediately via WhatsApp or email for cancellation requests. Refunds for cancelled orders are processed within 5-7 business days.'
                },
                {
                    question: 'How do I track my order?',
                    answer: 'Once your order is dispatched, you will receive a shipping confirmation email and SMS with a tracking link. You can also track your order by logging into your account and visiting the "My Orders" section. Tracking updates are refreshed in real-time via our Shiprocket integration. For made-to-order items, we provide regular production updates until dispatch.'
                }
            ]
        },
        {
            label: 'Shipping',
            questions: [
                {
                    question: 'How long does delivery take?',
                    answer: 'For ready-to-ship items: Standard shipping within India takes 5-7 business days, Express shipping takes 2-3 business days, and International shipping takes 7-14 business days. For made-to-order or handcrafted items, please allow an additional 7-21 business days for production before shipping. The estimated delivery date is shown on the product page and at checkout.'
                },
                {
                    question: 'Do you ship internationally?',
                    answer: 'Yes, we ship to over 30 countries worldwide. International shipping costs are calculated at checkout based on destination and package weight. Please note that customs duties and import taxes may apply depending on your country\u2019s regulations, and these are the responsibility of the buyer. International orders are shipped via tracked courier for security.'
                },
                {
                    question: 'Is shipping free?',
                    answer: 'Standard shipping within India is free on all orders above \u20B92,999. Orders below \u20B92,999 incur a flat shipping fee of \u20B9149. Express shipping is available at \u20B9499 regardless of order value. International shipping charges vary by destination and are calculated at checkout.'
                },
                {
                    question: 'How is my order packaged?',
                    answer: 'We take great care in packaging your handcrafted pieces. Each product is individually wrapped in acid-free tissue paper to preserve the fabric, placed in a reusable muslin pouch, and packed in a sturdy eco-friendly box. Silk items include silica gel packets for moisture protection. Our packaging is 100% recyclable and plastic-free, reflecting our commitment to sustainability.'
                },
                {
                    question: 'What if my order is delayed or lost?',
                    answer: 'In the rare event of a delivery delay, please contact our support team with your tracking number. We will coordinate with our logistics partner to resolve the issue promptly. If a shipment is confirmed lost by the carrier, we will either reship the product at no additional cost or process a full refund. You will be kept informed throughout the resolution process.'
                }
            ]
        },
        {
            label: 'Returns',
            questions: [
                {
                    question: 'What is your return/exchange policy?',
                    answer: 'We accept exchange requests within 7 days of delivery. Items must be unused, unwashed, and in their original condition with all tags, labels, and packaging intact. The authenticity certificate must also be included. Custom-made garments, intimate wear, and items marked as "Final Sale" are not eligible for exchange. Slight colour and texture variations in handcrafted products are not valid grounds for return.'
                },
                {
                    question: 'How do I initiate an exchange?',
                    answer: 'Log into your account, navigate to "My Orders", and click "Request Exchange" next to the relevant order. Alternatively, contact us via WhatsApp at +91 98765 43210 or email support@maati.in with your order number, the item you wish to exchange, and the preferred replacement. We will process your request within 24-48 hours and arrange a reverse pickup.'
                },
                {
                    question: 'Do I have to pay for return shipping?',
                    answer: 'For your first exchange on any order, return shipping is completely free \u2014 we will arrange a reverse pickup from your address. For subsequent exchanges on the same order, a nominal shipping fee of \u20B9149 may apply. Items that arrive damaged or defective are always exchanged free of charge.'
                },
                {
                    question: 'Can I get a refund instead of an exchange?',
                    answer: 'Yes, refunds are available for eligible items. Once we receive the returned product and complete our quality inspection (1-2 business days), the refund is processed within 5-7 business days. For online payments, the amount is credited to your original payment method. For COD orders, refunds are processed via bank transfer to the account details you provide.'
                },
                {
                    question: 'What if I receive a damaged or defective item?',
                    answer: 'We sincerely apologise if you receive a damaged item. Please contact us within 48 hours of delivery with photos of the damage. We will immediately arrange a free pickup and send a replacement or process a full refund, whichever you prefer. Our quality team will investigate the issue to prevent future occurrences.'
                }
            ]
        },
        {
            label: 'Custom Orders',
            questions: [
                {
                    question: 'Can I order a custom piece?',
                    answer: 'Absolutely! We specialise in connecting you with artisans for bespoke creations. You can customise fabric, colour, size, embroidery, and design details. Send us your requirements via WhatsApp or our Contact page, and our craft consultants will guide you through the process. Custom orders are a wonderful way to own a truly unique handcrafted piece while directly supporting an artisan.'
                },
                {
                    question: 'How long do custom orders take?',
                    answer: 'Custom order timelines depend on the craft and complexity: Block print garments take 10-15 days, Chikankari embroidery takes 15-20 days, Banarasi weaving takes 20-30 days, and Chanderi weaving takes 15-20 days. These timelines start after design approval and advance payment. Rush orders may be accommodated for an additional fee, subject to artisan availability.'
                },
                {
                    question: 'Is there a minimum order value for custom pieces?',
                    answer: 'There is no minimum order value for custom sizing on existing designs. For completely bespoke designs (new patterns, custom colour combinations, or unique embroidery), a minimum order value of \u20B95,000 applies. A 50% advance payment is required to begin production, with the balance due before shipping.'
                },
                {
                    question: 'Can I visit the artisans?',
                    answer: 'Yes! We organise "Craft Trail" experiences where you can visit artisan workshops in their villages. Currently, we offer trails in Jaipur (block printing), Varanasi (Banarasi weaving), Lucknow (Chikankari), and Kutch (Ajrakh and Bandhani). These immersive experiences let you see the craft process firsthand and meet the makers. Contact us for upcoming trail dates and booking information.'
                },
                {
                    question: 'Can I request a specific artisan for my order?',
                    answer: 'Yes, if you admire a particular artisan\u2019s work from our collection, you can request them for your custom order. Subject to availability, we will connect you with that artisan through our craft coordinators. This ensures your piece carries the distinct style and skill of the artisan you love.'
                }
            ]
        },
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

                .maati-faq-tab-scroll {
                    display: flex;
                    gap: 10px;
                    overflow-x: auto;
                    padding-bottom: 4px;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .maati-faq-tab-scroll::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                {/* Hero */}
                <section style={{
                    padding: '120px 24px 60px', borderBottom: '1px solid #E8DDD0',
                    textAlign: 'center',
                }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '50%',
                            backgroundColor: '#F3ECE4', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
                        }}>
                            <HelpCircle size={24} color="#C4572A" />
                        </div>
                        <h1 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: 400,
                            color: '#5C3D2E', marginBottom: '12px',
                        }}>
                            Frequently Asked Questions
                        </h1>
                        <p style={{ fontSize: '16px', color: '#8B7355', lineHeight: 1.6 }}>
                            Everything you need to know about our handcrafted products, ordering, and craft heritage
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: '900px', margin: '0 auto', padding: '48px 24px 80px' }}>

                    {/* Category Tabs */}
                    <div className="maati-faq-tab-scroll" style={{ marginBottom: '40px', justifyContent: 'center' }}>
                        {categories.map((cat, i) => (
                            <button
                                key={i}
                                onClick={() => {
                                    setActiveTab(i);
                                    setOpenIndex(null);
                                }}
                                style={{
                                    fontFamily: "'Source Sans 3', sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    padding: '10px 22px',
                                    backgroundColor: activeTab === i ? '#C4572A' : 'transparent',
                                    color: activeTab === i ? '#FFFFFF' : '#5C3D2E',
                                    border: activeTab === i ? '1px solid #C4572A' : '1px solid #E8DDD0',
                                    borderRadius: '30px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={(e) => {
                                    if (activeTab !== i) {
                                        e.currentTarget.style.borderColor = '#C4572A';
                                        e.currentTarget.style.color = '#C4572A';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (activeTab !== i) {
                                        e.currentTarget.style.borderColor = '#E8DDD0';
                                        e.currentTarget.style.color = '#5C3D2E';
                                    }
                                }}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* Accordion */}
                    <div style={{
                        backgroundColor: '#FFFFFF', borderRadius: '12px',
                        border: '1px solid #E8DDD0', overflow: 'hidden',
                    }}>
                        {categories[activeTab].questions.map((item, i) => (
                            <div
                                key={i}
                                style={{
                                    borderBottom: i < categories[activeTab].questions.length - 1 ? '1px solid #E8DDD0' : 'none',
                                }}
                            >
                                <button
                                    onClick={() => toggleAccordion(i)}
                                    style={{
                                        width: '100%',
                                        padding: '22px 28px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        background: openIndex === i ? '#FDFBF8' : 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        gap: '16px',
                                        transition: 'background-color 0.2s ease',
                                    }}
                                >
                                    <span style={{
                                        fontFamily: "'Source Sans 3', sans-serif",
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: openIndex === i ? '#C4572A' : '#5C3D2E',
                                        lineHeight: 1.4,
                                        transition: 'color 0.2s ease',
                                    }}>
                                        {item.question}
                                    </span>
                                    <div style={{
                                        width: '28px', height: '28px', borderRadius: '50%',
                                        backgroundColor: openIndex === i ? '#FFF0EB' : '#F3ECE4',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0, transition: 'all 0.3s ease',
                                    }}>
                                        {openIndex === i
                                            ? <Minus size={14} color="#C4572A" />
                                            : <Plus size={14} color="#5C3D2E" />
                                        }
                                    </div>
                                </button>

                                {openIndex === i && (
                                    <div style={{
                                        padding: '0 28px 22px',
                                        backgroundColor: '#FDFBF8',
                                    }}>
                                        <p style={{
                                            fontSize: '15px',
                                            color: '#5C3D2E',
                                            lineHeight: 1.8,
                                            margin: 0,
                                            opacity: 0.85,
                                        }}>
                                            {item.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Still Have Questions */}
                    <div style={{
                        marginTop: '48px', backgroundColor: '#F3ECE4',
                        borderRadius: '12px', padding: '40px', textAlign: 'center',
                    }}>
                        <h3 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: '28px', fontWeight: 500, color: '#5C3D2E',
                            margin: '0 0 12px 0',
                        }}>
                            Still Have Questions?
                        </h3>
                        <p style={{ fontSize: '15px', color: '#8B7355', marginBottom: '24px', lineHeight: 1.6 }}>
                            Our craft consultants are always happy to help. Reach out anytime for product queries, custom order guidance, or craft information.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <a
                                href="https://wa.me/919876543210"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '14px 28px', backgroundColor: '#25D366',
                                    color: '#FFFFFF', borderRadius: '8px', fontSize: '14px',
                                    fontWeight: 600, textDecoration: 'none',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                    transition: 'transform 0.2s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                            >
                                <MessageCircle size={18} /> Chat on WhatsApp
                            </a>
                            <button
                                onClick={() => navigate('/template22/contact')}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '14px 28px', backgroundColor: '#C4572A',
                                    color: '#FFFFFF', borderRadius: '8px', border: 'none',
                                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#D4683B';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#C4572A';
                                    e.currentTarget.style.transform = 'none';
                                }}
                            >
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default FAQs22;
